import {
    AccountAccessControlConfig,
    BoardExtendedData,
    DirectInvitationData,
    InvitationData,
    RangeResult,
    ResultData,
    SsoConfig, TeamWithMembersData, UserDetailData,
    UserLiteData,
    WorkspaceDetailData,
    WorkspaceLiteData
} from '../ApiTypes';
import { dateTransformer, get, post } from '../ApiUtil';
import { AccountLiteData } from './me';

/* -------------------------------------------------------------------------- */
/*                               Access Control                               */
/* -------------------------------------------------------------------------- */

const adminQueryAccessControlConfig = () => get<AccountAccessControlConfig>({
    path: '/admin.accessControl.info',
    transform: dateTransformer(),
});

export type AdminSaveAccessControlConfigBody = AccountAccessControlConfig;
const adminSaveAccessControlConfig = (body: AdminSaveAccessControlConfigBody) => post<ResultData>({
    path: '/admin.accessControl.save',
    body,
});

/* -------------------------------------------------------------------------- */
/*                              Account Settings                              */
/* -------------------------------------------------------------------------- */

const adminSaveAccountSettings = (body: Record<string, any>) => post<ResultData>({
    path: '/admin.accountSettings.save',
    body,
});

/* -------------------------------------------------------------------------- */
/*                                   Invites                                  */
/* -------------------------------------------------------------------------- */

const adminInvitesList = (type: string, target?: string) => get<InvitationData[]>({
    path: '/admin.invites.list',
    query: { type, target },
    transform: dateTransformer(),
});

export type AdminInvitesCreateBody = {
    expiry: string,
    type?: 'general' | 'workspace',
    target?: string,
};

const adminInvitesCreate = (body: AdminInvitesCreateBody) => post<InvitationData>({
    path: '/admin.invites.create',
    body,
    transform: dateTransformer(),
});

export type AdminInvitesSendBody = {
    emails: string[],
    type: string,
    workspaceIds: string[],
};

const adminInvitesSend = (body: AdminInvitesSendBody) => post<ResultData>({
    path: '/admin.invites.send',
    body,
    transform: dateTransformer(),
});

export type AdminInvitesDeleteBody = { inviteId: string };
const adminInvitesDelete = (body: AdminInvitesDeleteBody) => post<ResultData>({
    path: '/admin.invites.delete',
    body,
});

/* -------------------------------------------------------------------------- */
/*                                   Direct Invites                           */
/* -------------------------------------------------------------------------- */

const adminDirectInvitesList = (
    search = '',
    sort = 'roleName:asc',
    startAt = 0,
    maxResults = 20
) => get<RangeResult<DirectInvitationData>>({
    path: '/admin.directInvites.list',
    query: { search, sort, startAt, maxResults },
    transform: dateTransformer(),
});

const adminDirectInvitesCount = () => get<number>({
    path: '/admin.directInvites.count',
});

const adminApproveDirectInvites = (body: { inviteIds: string[] }) => post<{ executionId: string }>({
    path: '/admin.directInvites.approve',
    body,
});

const adminRejectDirectInvites = (body: { inviteIds: string[] }) => post<{ executionId: string }>({
    path: '/admin.directInvites.reject',
    body,
});

/* -------------------------------------------------------------------------- */
/*                             Approvals/Requests                             */
/* -------------------------------------------------------------------------- */

export interface AccountRequestActorData {
    id: string;
    name: string;
    email: string;
}

export interface AccountRequestInfo {
    id: string;
    type: string;
    state: string;
    params: Record<string, any>;
    creator: AccountRequestActorData;
    createdAt: Date;
    resolver?: AccountRequestActorData;
    resolvedAt?: Date;
}

const adminRequestsInfo = (requestId: string) => get<AccountRequestInfo>({
    path: '/admin.requests.info',
    query: { requestId },
    transform: dateTransformer(),
});

const adminRequestsList = (open: boolean) => get<AccountRequestInfo[]>({
    path: '/admin.requests.list',
    query: { open },
    transform: dateTransformer(),
});

const adminRequestsAction = (requestId: string, value: 'approve' | 'deny') => post<ResultData>({
    path: '/admin.requests.action',
    body: { requestId, value },
    transform: dateTransformer(),
});

/* -------------------------------------------------------------------------- */
/*                               Single Sign-On                               */
/* -------------------------------------------------------------------------- */

const adminQuerySsoConfig = () => get<SsoConfig>({
    path: '/admin.sso.info',
    transform: dateTransformer(),
});

export type AdminSaveSsoConfigBody = SsoConfig;
const adminSaveSsoConfig = (body: AdminSaveSsoConfigBody) => post<ResultData>({
    path: '/admin.sso.save',
    body,
});

const adminResetSsoConfig = () => post<ResultData>({
    path: '/admin.sso.reset',
});

/* -------------------------------------------------------------------------- */
/*                               Users / Teams                                */
/* -------------------------------------------------------------------------- */

export type UserLiteDataWithRole = UserLiteData & { roleName: string };
const adminUsersList = (search = '', sort = 'roleName:asc', startAt = 0, maxResults = 20) => get<RangeResult<UserLiteDataWithRole>>({
    path: '/admin.users.list',
    query: { search, sort, startAt, maxResults },
    transform: dateTransformer(),
});

export type UserStats = {
    adminCount: number,
    ownerCount: number,
    memberCount: number,
    guestCount: number,
    nonGuests: number,
};

const adminUsersStats = () => get<UserStats>({
    path: '/admin.users.stats',
});

const adminUsersInfo = (userId: string) => get<UserDetailData>({
    path: '/admin.users.info',
    query: { userId },
    transform: dateTransformer(),
});

type AdminUsersRemoveBody = { userId: string };
const adminUsersRemove = (body: AdminUsersRemoveBody) => post<ResultData>({
    path: '/admin.users.remove',
    body
});

const adminUsersRemove2 = (body: { userIds: string[] }) => post<{ executionId: string }>({
    path: '/admin.users.remove2',
    body
});

type AdminUsersSetRoleBody = { userId: string, roleName: string };
const adminUsersSetRole = (body: AdminUsersSetRoleBody) => post<ResultData>({
    path: '/admin.users.setRole',
    body
});

const adminUsersSetRole2 = (body: { userIds: string[], roleName: string }) => post<{ executionId: string }>({
    path: '/admin.users.setRole2',
    body
});

// Server:
// router.post('/api/v2/admin.boards.archive', roquMount(adminBoardsArchive));
// router.post('/api/v2/admin.boards.move', roquMount(adminBoardsMove));

const adminBoardsMove = (body: { boardIds: string[], toId: string }) => post<{ executionId: string }>({
    path: '/admin.boards.move',
    body
});

const adminBoardsDelete = (body: { boardIds: string[] }) => post<{ executionId: string }>({
    path: '/admin.boards.delete',
    body
});

const adminBoardsArchive = (body: { boardIds: string[] }) => post<{ executionId: string }>({
    path: '/admin.boards.archive',
    body
});

const adminWorkspacesList = () => get<WorkspaceLiteData[]>({
    path: '/admin.workspaces.list',
    transform: dateTransformer(),
});

const adminWorkspacesInfo = (workspaceId: string) => get<WorkspaceDetailData>({
    path: '/admin.workspaces.info',
    query: { workspaceId },
    transform: dateTransformer(),
});

const adminTeamList = (search = '', sort = 'createdAt:desc', startAt = 0, maxResults = 20) => get<RangeResult<TeamWithMembersData>>({
    path: '/admin.teams.list',
    query: { search, sort, startAt, maxResults },
    transform: dateTransformer(),
});

const adminTeamInfo = (teamId: string) => get<TeamWithMembersData>({
    path: '/admin.teams.info',
    query: { teamId },
    transform: dateTransformer(),
});

const adminBoardsList = (search: string, sort: string, startAt: number, maxResults: number) => get<RangeResult<BoardExtendedData>>({
    path: '/admin.boards.list',
    query: { search, sort, startAt, maxResults },
    transform: dateTransformer(),
});

export type AdminActivityType =
    'account.join' |
    'admin.invite.create' |
    'admin.invite.delete' |
    'admin.sso.reset' |
    'admin.sso.update' |
    'admin.accessControl.update' |
    'admin.accountSettings.update' |
    'admin.invite.sendBatch' |
    'admin.account.update' |
    'board.create' |
    'board.delete' |
    'board.archive' |
    'board.move' |
    'board.patch' |
    'board.join' |
    'board.addMember' |
    'board.removeMember' |
    'board.setSharing' |
    'board.connect' |
    'board.disconnect' |
    'workspace.create' |
    'workspace.delete' |
    'workspace.patch' |
    'workspace.join' |
    'workspace.leave' |
    'workspace.addMember' |
    'folder.create' |
    'folder.delete' |
    'folder.patch' |
    'account.removeUser' |
    'account.setUserRole' |
    'invite.send' |
    'invite.approve' |
    'invite.reject';

export interface AdminActivityData {
    actor: UserLiteData;
    id: string;
    type: AdminActivityType;
    actorId: string;
    accountId: string;
    data: Record<string, any>;
    createdAt: Date;
};

const adminActivityList = (search: string, sort: string, startAt: number, maxResults: number) => get<RangeResult<AdminActivityData>>({
    path: '/admin.activity.list',
    query: { search, sort, startAt, maxResults },
    transform: dateTransformer(),
});

/* -------------------------------------------------------------------------- */
/*                                  Billing                                   */
/* -------------------------------------------------------------------------- */

export interface AccountPlanData {
    name: string;
    label: string;
    features: Record<string, any>;
    overrides: Record<string, any>;
    expiresAt: Date;
    createdAt: Date;
}

export interface CardInfo {
    paymentMethod: 'card';
    cardType: 'master' | 'visa' | 'american_express' | 'discover' | 'jcb' | 'maestro' | 'diners_club' | 'unionpay';
    lastFourDigits: string;
    expiryDate: string;
}

export interface PaymentInfo {
    amount: number;
    currency: string;
    date: string;
}

export interface AccountSubscriptionData {
    quantity: number;
    state: 'active' | 'deleted';
    userEmail?: string;
    paymentInformation?: CardInfo;
    nextPayment?: PaymentInfo;
    lastPayment?: PaymentInfo;
    unitPrice?: string;
    interval?: string;
    updateUrl?: string;
    cancelUrl?: string;
    expiresAt?: Date;
    createdAt?: Date;
}

export interface AccountTrialData {
    state: 'active' | 'expired';
    lengthDays: number;
    expiresAt: Date;
    createdAt: Date;
}

export interface AccountPaymentData {
    id: number;
    amount: number;
    currency: string;
    payoutDate: string;
    isPaid: number;
    isOneOffCharge: boolean;
    receiptUrl: string;
}

export interface AccountBillingSettings {
    userLimit?: number;
    compSeats?: number;
}

export interface AccountBillingData {
    plan: AccountPlanData;
    subscription?: AccountSubscriptionData;
    trial?: AccountTrialData;
    settings: AccountBillingSettings;
}

export interface AccountHistoryNoteData {
    id: string;
    type: string;
    message: string;
    metadata: Record<string, any>;
    createdAt: Date;
}

const adminQueryBillingInfo = () => get<AccountBillingData>({
    path: '/admin.billing.info',
    transform: dateTransformer(),
});

const adminQueryBillingPayments = () => get<AccountPaymentData[]>({
    path: '/admin.billing.payments',
    transform: dateTransformer(),
});

const adminQueryBillingHistory = () => get<AccountHistoryNoteData[]>({
    path: '/admin.billing.history',
    transform: dateTransformer(),
});

type AdminBillingSetQuantityBody = { quantity: number };
const adminBillingSetQuantity = (body: AdminBillingSetQuantityBody) => post<ResultData>({
    path: '/admin.billing.setQuantity',
    body,
});

export type AdminIntegrationData = {
    name: string,
    label: string,
    description: string,
    enabled: boolean,
    metadata: Record<string, any>,
};

const adminIntegrationsList = () => get<AdminIntegrationData[]>({
    path: '/admin.integrations.list',
});

/* -------------------------------------------------------------------------- */
/*                                   General                                  */
/* -------------------------------------------------------------------------- */

export interface AdminGeneralInfoData {
    subscription: boolean;
    users: {
        owners: number,
        admins: number,
        members: number,
        guests: number,
    };
    workspaces: number;
    boards: number;
    objects: number;
    stickys: number;
}

const adminQueryGeneralInfo = () => get<AdminGeneralInfoData>({
    path: '/admin.general.info',
});

const adminUpdateGeneralInfo = (body: { label: string }) => post<AccountLiteData>({
    path: '/admin.general.update',
    body,
});

export const adminEndpoints = {
    accessControl: {
        info: adminQueryAccessControlConfig,
        save: adminSaveAccessControlConfig,
    },
    accountSettings: {
        save: adminSaveAccountSettings,
    },
    invites: {
        list: adminInvitesList,
        create: adminInvitesCreate,
        send: adminInvitesSend,
        delete: adminInvitesDelete,
    },
    directInvites: {
        list: adminDirectInvitesList,
        count: adminDirectInvitesCount,
        approve: adminApproveDirectInvites,
        reject: adminRejectDirectInvites,
    },
    requests: {
        info: adminRequestsInfo,
        list: adminRequestsList,
        action: adminRequestsAction,
    },
    sso: {
        info: adminQuerySsoConfig,
        save: adminSaveSsoConfig,
        reset: adminResetSsoConfig,
    },
    billing: {
        info: adminQueryBillingInfo,
        payments: adminQueryBillingPayments,
        history: adminQueryBillingHistory,
        setQuantity: adminBillingSetQuantity,
    },
    general: {
        info: adminQueryGeneralInfo,
        update: adminUpdateGeneralInfo,
    },
    users: {
        info: adminUsersInfo,
        list: adminUsersList,
        stats: adminUsersStats,
        remove: adminUsersRemove,
        remove2: adminUsersRemove2,
        setRole: adminUsersSetRole,
        setRole2: adminUsersSetRole2,
    },
    teams: {
        list: adminTeamList,
        info: adminTeamInfo,
    },
    boards: {
        list: adminBoardsList,
        move: adminBoardsMove,
        delete: adminBoardsDelete,
        archive: adminBoardsArchive,
    },
    activity: {
        list: adminActivityList,
    },
    workspaces: {
        info: adminWorkspacesInfo,
        list: adminWorkspacesList,
    },
    integrations: {
        list: adminIntegrationsList,
    }
};
